import React, { useState } from "react";
import { toast } from "sonner";
import useFetch from "@/lib/useFetch";
import api from "@/lib/api";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Megaphone, Send } from "lucide-react";
import { timeAgo } from "@/lib/communities";

const PRIORITIES = ["normal", "high", "critical"];

export default function Announcements() {
    const { data: communities } = useFetch("/communities");
    const { data: anns, reload } = useFetch("/announcements");
    const [form, setForm] = useState({
        community_key: "", title: "", message: "", priority: "normal",
        to_dashboard: true, to_discord: true, to_push: false, notify_members: true,
    });
    const [busy, setBusy] = useState(false);
    if (!communities || !anns) return <Loader />;
    const upd = (k, v) => setForm({ ...form, [k]: v });
    const nameFor = (k) => communities.find((c) => c.key === k)?.name || "Network";

    const publish = async () => {
        if (!form.title || !form.message) { toast.error("Title and message required"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/announcements", { ...form, community_key: form.community_key || null });
            reload();
            if (form.to_discord) {
                const ok = data.discord?.success;
                toast[ok ? "success" : "warning"]("Announcement published",
                    { description: ok ? `Posted to ${data.announcement.discord_channel}` : (data.discord?.error || "Discord post skipped") });
            } else {
                toast.success("Announcement published to dashboard");
            }
            setForm({ ...form, title: "", message: "" });
        } catch (e) {
            toast.error("Publish failed", { description: e.response?.data?.detail || "" });
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="admin-announcements">
            <PageHeader title="Announcements" subtitle="Compose and broadcast to dashboard, Discord and push." />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <SectionCard title="Composer" testId="announcement-composer">
                    <div className="space-y-3">
                        <div>
                            <label className="label-mono text-muted-foreground">Community</label>
                            <Select value={form.community_key || "network"} onValueChange={(v) => upd("community_key", v === "network" ? "" : v)}>
                                <SelectTrigger data-testid="ann-community" className="bg-background border-border mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="network">Network-wide</SelectItem>
                                    {communities.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="label-mono text-muted-foreground">Title</label>
                            <Input data-testid="ann-title" value={form.title} onChange={(e) => upd("title", e.target.value)}
                                className="bg-background border-border mt-1" placeholder="PURGE BEGINS IN 24 HOURS" />
                        </div>
                        <div>
                            <label className="label-mono text-muted-foreground">Message</label>
                            <Textarea data-testid="ann-message" value={form.message} onChange={(e) => upd("message", e.target.value)}
                                className="bg-background border-border mt-1" rows={4} placeholder="Prepare your base. End-of-month purge begins tomorrow." />
                        </div>
                        <div>
                            <label className="label-mono text-muted-foreground">Priority</label>
                            <Select value={form.priority} onValueChange={(v) => upd("priority", v)}>
                                <SelectTrigger data-testid="ann-priority" className="bg-background border-border mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            {[
                                ["to_dashboard", "Post to Dashboard"], ["to_discord", "Post to Discord"],
                                ["to_push", "Send Mobile Push"], ["notify_members", "Notify Members"],
                            ].map(([k, label]) => (
                                <label key={k} className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                                    <Checkbox data-testid={`ann-${k}`} checked={form[k]} onCheckedChange={(v) => upd(k, !!v)} /> {label}
                                </label>
                            ))}
                        </div>
                        <Button data-testid="ann-publish" disabled={busy} onClick={publish}
                            className="w-full font-heading uppercase bg-hazard text-black hover:bg-hazard/90">
                            <Send size={15} className="mr-1.5" /> Publish Announcement
                        </Button>
                    </div>
                </SectionCard>

                <SectionCard title="Recent" testId="announcement-list">
                    <div className="space-y-3">
                        {anns.map((a) => (
                            <div key={a.id} className="rounded-md border border-border bg-background/40 p-3">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Megaphone size={15} className="text-hazard" />
                                    <span className="font-heading font-semibold uppercase text-foreground">{a.title}</span>
                                    <span className="label-mono text-muted-foreground">{nameFor(a.community_key)}</span>
                                    {a.discord_status && <StatusBadge status={a.discord_status} label={`DISCORD ${a.discord_status.toUpperCase()}`} />}
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">{a.message}</p>
                                <span className="label-mono text-muted-foreground">{a.author} · {timeAgo(a.created_at)}</span>
                            </div>
                        ))}
                    </div>
                </SectionCard>
            </div>
            <Footer />
        </div>
    );
}
